import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { logOtherEvent, OtherLogEvents } from "../../api/analitics";
import { PLAYER_EVENTS, useAudioContext } from "../../contexts/AudioContext";
import { selectorSetting } from "../../store/settings/selectors";
import { SETTING } from "../../utils/settings/constants";
import { useAnimation } from "../useAnimation";
import { selectorPlayerWaveformsDictionary } from "../../store/player/selectors";
import {
  isNumberBetweenEquals,
  toFixedWithoutRounding
} from "@/shared/utils/number/number";
import { isBrowserSupportsFloatValueForScrollTop } from "@/shared/utils/dom";
import {
  getParaWrapAndContentElementsByIdAndView,
  getTopMiddleOfParaContentFirstLine
} from "@/shared/utils/reader/reader-content";

const useReaderLiveReading = ({
  isEnabled = true,
  refScrollView,
  triggerRestart1,
  triggerRestart2,
  currentPlayingPara,
  getArrowPosition,
  onUpdateArrowPosition,
  onUpdatePanel,
  onAnimationStopped,
}) => {
  const refOnAnimationStopped = useRef(onAnimationStopped);
  refOnAnimationStopped.current = onAnimationStopped;

  const refStepLiveRead = useRef(undefined);

  const currentPlayingParaId = currentPlayingPara?.id;

  const {
    audioChapterId,
    playbackRate,
    getPlayerCurrentTime,
    subscribeToPlayerEvents,
    unsubscribeFromPlayerEvents,
    isPlaying,
  } = useAudioContext();
  
  const animate = useAnimation();

  const syncPlayerAndReader = useSelector(selectorSetting(SETTING.syncPlayerAndReader.id));
  const readerFontSize = useSelector(selectorSetting(SETTING.readerFontSize.id));
  const readerFontFamily = useSelector(selectorSetting(SETTING.readerFontFamily.id));
  const zoom = useSelector(selectorSetting(SETTING.zoom.id));

  const waveformsDictionary = useSelector(selectorPlayerWaveformsDictionary);
  const parasPositionInAudio = waveformsDictionary[audioChapterId]?.paragraphsAudioPositions;

  const [isLiveReading, setIsLiveReading] = useState(false);
  const [triggerLiveReadRestart, setTriggerLiveReadRestart] = useState(0);

  const isReadyAndWaitingToLiveRead =
    isPlaying
    && !!syncPlayerAndReader
    && !!audioChapterId
    && !!currentPlayingParaId;

  const openCurrentPara = async () => {
    if (!currentPlayingPara) {
      return null;
    }

    const activeValueNew = currentPlayingParaId;
    await onUpdatePanel(activeValueNew);
    return activeValueNew;
  };


  const getElementScrollerView = () => {
    return refScrollView.current?.view;
  };

  const getReadOffset = () => {
    const elView = getElementScrollerView();

    if (!elView) {
      return undefined;
    }

    const value = elView.offsetHeight / 3;

    if (value < 165) { // Mobile UX
      return elView.offsetHeight / 2.5;
    }

    return value;
  };

  const getParaWrapAndContentElementsBy = (id) => {
    const scrollerView = getElementScrollerView();
    return getParaWrapAndContentElementsByIdAndView(id, scrollerView);
  };

  const _scrollView = (scrollTop, styleTop) => {
    const elView = getElementScrollerView();

    if (elView) {
      elView.scrollTop = scrollTop;
      elView.style.top = styleTop;
    }
  }

  const scrollByValues = (arrowStyleTop, viewScrollTop, viewStyleTop) => {
    _scrollView(viewScrollTop, viewStyleTop);
    onUpdateArrowPosition(arrowStyleTop);
  };

  const getStartToEndAnimationInfo = () => {
    const {
      start: timeStart,
      end: timeEnd,
      id: id
    } = currentPlayingPara;
    const durationFull = timeEnd - timeStart;

    const paraElementObj = getParaWrapAndContentElementsBy(id);
    if (!paraElementObj || !paraElementObj.paraWrap || !paraElementObj.paraContentOnly) {
      return null;
    }

    const { paraWrap, paraContentOnly } = paraElementObj;

    const {
      isSingleLineOfTextContent, middleTop, fontSize,
    } = getTopMiddleOfParaContentFirstLine(paraWrap, paraContentOnly);

    const scrollTopPara = paraWrap.offsetTop + middleTop;

    const distanceFull = isSingleLineOfTextContent ? 0 : (paraContentOnly.offsetHeight - fontSize);

    const timeListened = Math.max(getPlayerCurrentTime() - timeStart, 0.1);
    const time = durationFull - timeListened;
    const offsetListened = (timeListened * distanceFull) / durationFull;


    const scrollTopStart = scrollTopPara + offsetListened;

    const distance = (distanceFull - offsetListened);
    const duration = (time * 1000) / playbackRate;
    
    return {
      scrollTopStart,
      distance,
      duration,
    };
  };

  const getAnimationVariablesForFrame = (scrollTop) => {
    const elView = getElementScrollerView();
    const readOffset = getReadOffset();

    const scrollTopMax = elView.scrollHeight - elView.offsetHeight;

    let offsetArrow;
    let isMovingArrowInsteadOfScrolling;

    if (scrollTop < readOffset) {
      // Don't scrollByValues, move pointer at the beginning.
      offsetArrow = scrollTop;
      isMovingArrowInsteadOfScrolling = true;
    } else if (scrollTop > scrollTopMax + readOffset) {
      // Don't scrollByValues, move pointer at the end.
      offsetArrow = scrollTop - scrollTopMax;
      isMovingArrowInsteadOfScrolling = true;
    } else {
      // Scroll view and set pointer at the read zone offset.
      offsetArrow = readOffset;
      isMovingArrowInsteadOfScrolling = false;
    }

    const scrollTopNewFloat = scrollTop - readOffset;
    const scrollTopNewInt = parseInt(toFixedWithoutRounding(scrollTopNewFloat));

    const deltaScrollTopOldToNew = scrollTopNewFloat - parseInt(toFixedWithoutRounding(elView.scrollTop));

    const isApplyCSSWorkaroundForSmoothScrolling = (
      !isBrowserSupportsFloatValueForScrollTop
      && !isMovingArrowInsteadOfScrolling
      && deltaScrollTopOldToNew < 1.3

      // if without it, then scrolls with a random content deformation in Firefox 80% zoom
      // but with it – scrolls by jerkily
      && Number.isInteger(devicePixelRatio)
    );

    let viewScrollTop, viewStyleTop;

    if (isApplyCSSWorkaroundForSmoothScrolling) {
      const floatPart = parseFloat((scrollTopNewFloat - scrollTopNewInt).toFixed(1)) >= 0.5 ? 0.5 : 0;

      viewScrollTop = scrollTopNewInt;
      viewStyleTop = -floatPart + "px";
    } else {
      viewScrollTop = scrollTopNewFloat;
      viewStyleTop = "0px";
    }

    return {
      arrowStyleTop: offsetArrow,
      viewScrollTop,
      viewStyleTop,
    };
  };


  const playFromCurrentToStartAnimation = (scrollTopStart, fnCheckIfAnimationActual) => {
    return new Promise((resolve) => {
      const elView = getElementScrollerView();

      const arrowStart = getArrowPosition();
      const viewStart = elView.scrollTop;
      const viewStyleTopStart = elView.style.top;

      const {
        arrowStyleTop: arrowFinal,
        viewScrollTop: viewFinal,
        viewStyleTop: viewStyleTopFinal,
      } = getAnimationVariablesForFrame(scrollTopStart);

      const distanceArrow = arrowFinal - arrowStart;
      const distanceView = viewFinal + parseInt(viewStyleTopFinal)
        - viewStart + parseInt(viewStyleTopStart);
      const distanceArrowABS = Math.abs(arrowFinal - arrowStart);
      const distanceViewABS = Math.abs(
        viewFinal + parseInt(viewStyleTopFinal) - viewStart + parseInt(viewStyleTopStart)
      );

      const isDistanceArrowGreater = distanceArrowABS > distanceViewABS;

      const distance = isDistanceArrowGreater ? distanceArrowABS : distanceViewABS;

      if (isNumberBetweenEquals(distance, 1.5, elView.offsetHeight)) {
        const duration = Math.max(Math.abs(distance * 2), 300);

        return animate(duration, (timePassed) => {
          if (!fnCheckIfAnimationActual()) {
            return;
          }

          const coeff = timePassed / duration;
          scrollByValues(
            arrowStart + distanceArrow * coeff,
            viewStart + distanceView * coeff,
            "0px"
          )
        }, resolve).stop;
      } else {
        scrollByValues(arrowFinal, viewFinal, viewStyleTopFinal)
        resolve();
      }
    });
  };

  const playFromStartToEndAnimation = (
    distance, duration, scrollTopStart,
    checkIfAsyncOperationIsStillActual,
  ) => {
    let isFirstStepPassed = false;
    let isStepLiveReadDefined = false;

    return animate(duration, async (timePassed) => {
      if (!checkIfAsyncOperationIsStillActual()) {
        return;
      }

      if (!isFirstStepPassed && timePassed) {
        isFirstStepPassed = true;
      }

      if (isFirstStepPassed && !isStepLiveReadDefined) {
        refStepLiveRead.current = (distance * timePassed) / duration;
        isStepLiveReadDefined = true;
      }

      const newScrollTop = scrollTopStart + (distance * timePassed) / duration;
      const { arrowStyleTop, viewScrollTop, viewStyleTop } = getAnimationVariablesForFrame(newScrollTop);
      scrollByValues(arrowStyleTop, viewScrollTop, viewStyleTop)
    }).stop;
  };


  // Main algorithm
  useEffect(() => {
    let fnStopAnimation, isEffectCallbackCalled, isLiveReadingWasEnabled;

    if (
      isReadyAndWaitingToLiveRead
      && currentPlayingPara
      && isEnabled
    ) {
      openCurrentPara().then(async () => {
        logOtherEvent(OtherLogEvents.READER_ACTION, {
          id: "auto_read_while_listen",
        });

        if (isEffectCallbackCalled) {
          return;
        }


        const variables = getStartToEndAnimationInfo();
        if (!variables) {
          return;
        }

        const { duration, distance, scrollTopStart } = variables;

        const isOperationActual = () => !isEffectCallbackCalled;

        fnStopAnimation = await playFromCurrentToStartAnimation(scrollTopStart, isOperationActual);

        fnStopAnimation = playFromStartToEndAnimation(distance, duration, scrollTopStart, isOperationActual)

        setIsLiveReading(() => {
          isLiveReadingWasEnabled = true;
          return true;
        });
      });
    }

    return () => {
      isEffectCallbackCalled = true;

      refStepLiveRead.current = undefined;

      if (fnStopAnimation) {
        fnStopAnimation();
      }

      if (isLiveReadingWasEnabled) {
        setIsLiveReading(false);
      }

      if (refOnAnimationStopped.current) {
        refOnAnimationStopped.current();
      }
    };
  }, [
    isEnabled,
    triggerLiveReadRestart,
    isReadyAndWaitingToLiveRead,

    audioChapterId,
    currentPlayingPara,
    parasPositionInAudio,

    triggerRestart1, triggerRestart2,
    playbackRate, zoom,
    readerFontSize, readerFontFamily,

    animate,
  ]);

  // Trigger live reading restart on player skip.
  useEffect(() => {
    const listener = (e) => {
      if (e.event === PLAYER_EVENTS.SKIP) {
        setTriggerLiveReadRestart(Math.random());
      }
    };

    subscribeToPlayerEvents(listener);
    return () => {
      unsubscribeFromPlayerEvents(listener);
    };
  }, []);

  return {
    refStepLiveRead,
    isReadyAndWaitingToLiveRead,
    isLiveReading,
    getReadOffset,
  };
};

export default useReaderLiveReading;
